// MeteogramLayout – vyhledávání bodu (obec/letiště/stanice ČHMÚ/lyžařské středisko/vodní plocha)
// a zobrazení meteogramu ALADIN (72h předpověď). Fulltext přes /api/meteogram_points,
// běhy modelu přes /api/meteogram_runs, obrázek grafu přes /api/chart?type=meteogram.
// Poslední vybraný bod se pamatuje v localStorage (useLastMeteogramPoints) a při návratu
// na stránku se rovnou vybere i s nejnovějším během.
import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Spinner } from '@heroui/react'
import { useMeteogramSearch, useMeteogramRuns } from '../hooks/useMeteogram'
import { getLastMeteogramPoint, getLastMeteogramPoints, addLastMeteogramPoint } from '../hooks/useLastMeteogramPoints'
import { ChartImage } from '../components/reports/shared/ChartImage'
import { UserIcon } from '../components/auth/UserIcon'
import { useTrackView } from '../hooks/useTrackProduct'

// Stejný vzor jako u ostatních grafů (GrafView/HlyGrafView/…) — CHART_BASE respektuje
// VITE_API_URL (produkce s absolutní URL backendu, viz .env.kwdata/.env.production), s
// fallbackem na relativní '/api' pro dev (Vite proxy). Bez tohohle prefixu by fetch na produkci
// šel na origin frontendové domény místo backendu (žádný '/api' tam neexistuje → SPA fallback).
const CHART_BASE = import.meta.env.VITE_API_URL || '/api'

const CATEGORY_LABELS = {
  A: 'Obec',
  B: 'Letiště',
  C: 'Stanice ČHMÚ',
  D: 'Lyžařské středisko',
  E: 'Vodní plocha',
}

const CATEGORY_OPTIONS = [
  { value: '', label: 'Vše' },
  { value: 'A', label: 'Obce' },
  { value: 'B', label: 'Letiště' },
  { value: 'C', label: 'ČHMÚ místa' },
  { value: 'D', label: 'Lyžařská střediska' },
  { value: 'E', label: 'Vodní plochy' },
]

// Zobrazovaný název bodu ve vyhledávacím políčku po výběru — "Adamov (okr. Blansko)".
function pointDisplayName(point) {
  if (!point) return ''
  return point.extra ? `${point.name} (${point.extra})` : point.name
}

function PointSearchBox({ selectedPoint, onSelect }) {
  const [inputValue, setInputValue] = useState(() => pointDisplayName(selectedPoint))
  const [category, setCategory] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const { results, isLoading } = useMeteogramSearch(open ? inputValue : '', category || null)

  // Když se zvenku (localStorage při startu) nastaví selectedPoint, zobraz jeho název v poli.
  useEffect(() => {
    setInputValue(pointDisplayName(selectedPoint))
  }, [selectedPoint])

  const confirmSelect = (point) => {
    setInputValue(pointDisplayName(point))
    setOpen(false)
    setActiveIndex(-1)
    onSelect(point)
  }

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const idx = activeIndex >= 0 ? activeIndex : 0
      if (results[idx]) confirmSelect(results[idx])
    } else if (e.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
    }
  }

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      listRef.current.children[activeIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  return (
    <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
      <select
        value={category}
        onChange={e => setCategory(e.target.value)}
        className="text-sm px-2 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-blue-400"
      >
        {CATEGORY_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <div className="relative flex-1">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder="Zadejte místo pro meteogram…"
          className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          onChange={e => {
            setInputValue(e.target.value)
            setActiveIndex(-1)
            setOpen(true)
          }}
          onFocus={e => { e.target.select(); setOpen(true) }}
          onBlur={() => setTimeout(() => {
            setOpen(false)
            // Bez potvrzeného výběru se pole při odchodu vrátí k naposledy vybranému bodu.
            setInputValue(pointDisplayName(selectedPoint))
          }, 150)}
          onKeyDown={handleKeyDown}
        />
        {open && (isLoading || results.length > 0) && (
          <ul
            ref={listRef}
            className="absolute z-50 top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto text-sm"
          >
            {isLoading && (
              <li className="px-3 py-2 text-gray-400 flex items-center gap-2">
                <Spinner size="sm" /> Hledám…
              </li>
            )}
            {!isLoading && results.map((p, i) => (
              <li
                key={p.id}
                className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${i === activeIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}
                onMouseDown={() => confirmSelect(p)}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded shrink-0">
                  {CATEGORY_LABELS[p.category] || p.category}
                </span>
                <span className="font-medium">{p.name}</span>
                {p.extra && <span className="text-gray-400 text-xs">{p.extra}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function RunSelect({ runs, selectedRunDt, onSelect }) {
  if (runs.length === 0) return null
  return (
    <select
      value={selectedRunDt || ''}
      onChange={e => onSelect(e.target.value)}
      className="text-sm px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-blue-400"
    >
      {runs.map(r => (
        <option key={r.run_dt} value={r.run_dt}>
          {r.run_dt_local} ({r.subdir} UTC)
        </option>
      ))}
    </select>
  )
}

// Vybere bod podle id předaného z hlavního menu (klik na "Poslední místa"), jinak naposledy vybraný.
function initialPoint(navigatedPointId) {
  if (navigatedPointId) {
    const fromList = getLastMeteogramPoints().find(p => p.id === navigatedPointId)
    if (fromList) return fromList
  }
  return getLastMeteogramPoint()
}

export function MeteogramLayout() {
  const navigate = useNavigate()
  // Sledování přístupů (etapa K2) — meteogram je systémová část bez uzlu v registru.
  useTrackView('meteogram', 'meteogram')
  const location = useLocation()
  const [selectedPoint, setSelectedPoint] = useState(() => initialPoint(location.state?.pointId))
  const [selectedRunDt, setSelectedRunDt] = useState(null)
  const { runs, isLoading: runsLoading } = useMeteogramRuns(4)

  const handleSelectPoint = useCallback((point) => {
    setSelectedPoint(point)
    setSelectedRunDt(null) // vybere se default (nejnovější) níže, jakmile runs dorazí
    addLastMeteogramPoint(point)
  }, [])

  // Jakmile jsou runs načtené a žádný běh není vybraný, vyber nejnovější (runs[0]).
  const effectiveRunDt = selectedRunDt || runs[0]?.run_dt || null

  const chartUrl = selectedPoint && effectiveRunDt
    ? `${CHART_BASE}/chart?type=meteogram&cfg=default&point=${encodeURIComponent(selectedPoint.id)}&run_dt=${encodeURIComponent(effectiveRunDt)}&format=png`
    : null

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center p-4 sm:p-6 gap-4 sm:gap-6">
      <div className="fixed top-2 left-2 sm:top-4 sm:left-4 z-40 flex items-center gap-2">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors text-sm text-gray-700"
          title="Domů"
        >
          <span className="text-lg leading-none">🏠</span> <span className="hidden sm:inline">Domů</span>
        </button>
        <UserIcon variant="compact" placement="bottom-left" />
      </div>

      <div className="text-center mt-10 sm:mt-0">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Meteogramy ALADIN</h1>
        <p className="text-gray-400 mt-1 text-sm sm:text-base">72hodinová předpověď modelu ALADIN CZ_1km</p>
      </div>

      <div className="w-full max-w-2xl flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="flex-1">
          <PointSearchBox selectedPoint={selectedPoint} onSelect={handleSelectPoint} />
        </div>
        {runsLoading ? (
          <Spinner size="sm" />
        ) : (
          <RunSelect runs={runs} selectedRunDt={effectiveRunDt} onSelect={setSelectedRunDt} />
        )}
      </div>

      {chartUrl && (
        <div className="w-full max-w-4xl flex justify-center">
          <ChartImage url={chartUrl} />
        </div>
      )}
    </div>
  )
}
